import { audioContext } from '@/audio/context';
import { qa } from '@/qa/recorder';
import { missingFeatures } from '@/audio/support';
import { strings } from '@/content/strings';
import { recordedSheets } from '@/audio/recorded';
import { SheetVoice, SimulatedVoice, WebVoice } from '@/audio/voice';
import type { PracticeWord, WordSet } from '@/content/words';
import type { CoachWord } from '@/game/coach/coach';
import { PracticeSession, type ListenerControl } from '@/game/practiceSession';
import { EXERCISES } from '@/content/exercises';
import { ageBand, youngVoice } from '@/game/settings';
import { Listener } from '@/speech/audio/listener';
import { openMicrophone, SimulatedMicrophone, type AudioInput } from '@/speech/audio/sources';
import { decodeWav } from '@/speech/dsp/wav';
import { WorkerScorer } from '@/speech/engine/workerScorer';
import { Understanding } from '@/speech/understand/understanding';

/**
 * Speech-to-text for spoken answers to the helper's questions, one worker per page, started on
 * the first question (most sessions never need it).
 */
let shared: Understanding | undefined;
function understanding(): Understanding {
  shared ??= new Understanding();
  return shared;
}

export function toCoachWord(word: PracticeWord): CoachWord {
  return {
    id: word.id,
    text: word.text,
    phonemes: word.segments.map((s) => s.phoneme),
    syllable: word.syllable,
    // A practice syllable is judged as one (its own threshold, no easier step below it).
    ...(word.kind ? { kind: word.kind } : {}),
  };
}

export interface SessionSetup {
  session: PracticeSession;
  /** Set when the microphone is simulated with dataset recordings (`?mic=sim`). */
  simulatedMic: SimulatedMicrophone | undefined;
}

/**
 * Wires a practice session to the browser: the scorer worker, Web Speech voice, and either the
 * real microphone or (with `?mic=sim`, for development and tests) a simulated one that plays
 * real recordings.
 */
export interface SessionOptions {
  /** Replace the microphone with real dataset recordings. */
  simulatedMic: boolean;
  /** Replace text-to-speech with a timed stand-in (no speaker attached). */
  simulatedVoice: boolean;
  silenceMs?: number | undefined;
  /**
   * With both simulations: real adult speech leaks into the simulated microphone at this gain
   * whenever the voice speaks, like speakers heard by the microphone (echo tests).
   */
  echoGain?: number | undefined;
  /** The player's age (grown-up setting): coach phrasing and the young-voice threshold. */
  age?: number | null | undefined;
}

export function createPracticeSession(
  wordSet: WordSet,
  { simulatedMic: simulated, simulatedVoice, silenceMs, echoGain, age = null }: SessionOptions,
): SessionSetup {
  const missing = missingFeatures({ microphone: !simulated });
  const simulatedMic = simulated && missing.length === 0 ? new SimulatedMicrophone() : undefined;
  const scorer = new WorkerScorer();
  const echo = simulatedMic && echoGain ? echoClips(simulatedMic, echoGain) : undefined;
  const voice = new SimulatedVoice(audioContext, undefined, echo);
  if (simulated) {
    // Test hook: score a 16 kHz WAV recording directly with the in-browser scorer.
    Object.assign(window, {
      foeSpoken: voice.spoken,
      foeScoreClip: async (url: string, targetPhonemes: string[], kind: 'word' | 'syllable') => {
        const { samples } = decodeWav(await (await fetch(url)).arrayBuffer());
        const started = performance.now();
        const result = await scorer.score({ audio: samples, targetPhonemes, kind });
        return { result, ms: performance.now() - started };
      },
    });
  }
  const session = new PracticeSession({
    ...(missing.length > 0 ? { unsupported: strings.feedback.unsupported(missing) } : {}),
    scorer,
    ageBand: ageBand(age),
    young: youngVoice(age),
    ...(silenceMs ? { silenceMs } : {}),
    voice: simulatedVoice
      ? voice
      : new SheetVoice(audioContext, new WebVoice(audioContext), recordedSheets()),
    words: wordSet.words.map(toCoachWord),
    transcribe: async (audio) => (await understanding().transcribe(audio)).text,
    prepareAnswers: () => {
      void understanding()
        .load('stt')
        .catch(() => undefined);
    },
    exercises: (word) => EXERCISES[word.text],
    choices: true,
    // A short briefing before the first word (the backstory is the intro screen's).
    briefing: 'mission',
    sound: { phoneme: wordSet.sound.phoneme, label: wordSet.sound.label.replace(/\//g, '') },
    startListening: async (callbacks): Promise<ListenerControl> => {
      const context = audioContext();
      if (context.state === 'suspended') await context.resume();
      // The capture worklet must be loaded before the microphone opens (iOS playback distortion).
      await Listener.prepare(context);
      // The simulated microphone outlives a mic-off, like a physical device.
      const input: AudioInput = simulatedMic
        ? { stream: simulatedMic.stream, close: () => undefined }
        : await openMicrophone();
      const track = input.stream.getAudioTracks()[0];
      qa.audioInfo({
        contextSampleRate: context.sampleRate,
        ...(track ? { track: track.getSettings() } : {}),
      });
      let listener: Listener;
      try {
        listener = await Listener.start(input.stream, callbacks, context);
      } catch (error) {
        input.close(); // release the microphone (and the browser's recording indicator)
        throw error;
      }
      return {
        setPrompting: (p, mark) => {
          listener.setPrompting(p, mark);
        },
        setHangover: (ms) => {
          listener.setHangover(ms);
        },
        hearingVoice: () => listener.hearingVoice,
        noiseFloorDb: () => listener.noiseFloorDb,
        setMuted: (m) => {
          listener.setMuted(m);
        },
        close: async () => {
          await listener.close();
          input.close();
        },
      };
    },
  });
  return { session, simulatedMic };
}

/** Leaks real adult recordings (from the simulator manifest) into the simulated microphone. */
function echoClips(mic: SimulatedMicrophone, gain: number): (ms: number) => () => void {
  let urls: string[] = [];
  let next = 0;
  void fetch('/__sim/percept-r/manifest.json')
    .then((r) => r.json() as Promise<{ echo?: string[] }>)
    .then((m) => {
      urls = m.echo ?? [];
    });
  return (ms) => {
    const url = urls[next++ % Math.max(1, urls.length)];
    if (!url) return () => undefined;
    const stop = mic.leak(url, gain, ms);
    return () => {
      void stop.then((end) => {
        end();
      });
    };
  };
}
